/**
 * Price Range Hook
 * 
 * Fetches the global price range for products based on filters.
 * Used for initializing price filter defaults and displaying min/max bounds.
 * 
 * Usage:
 * ```typescript
 * const { priceRange, isLoading } = usePriceRange({
 *   categoryId: 'cat-123',
 *   searchQuery: 'nike shoes'
 * });
 * 
 * // Use in filter defaults
 * const [filters, setFilters] = useState({
 *   priceRange: {
 *     min: priceRange?.min ?? 0,
 *     max: priceRange?.max ?? 1000
 *   }
 * });
 * ```
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '../../lib/supabaseClient';
import { PRODUCT_CACHE_TIME } from './queryKeys';

export interface UsePriceRangeOptions {
  categoryId?: number | null;
  searchQuery?: string | null;
  enabled?: boolean;
}

export interface PriceRangeResult {
  priceRange: { min: number; max: number } | null;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * Fetch global price range for filter initialization
 * 
 * @param options - Category ID and search query for filtering
 * @returns Price range with loading/error states
 */
export function usePriceRange(
  options: UsePriceRangeOptions = {}
): PriceRangeResult {
  const {
    categoryId,
    searchQuery,
    enabled = true,
  } = options;

  // Build cache key
  const cacheKey = ['priceRange', categoryId, searchQuery] as const;

  const priceRangeQuery = useQuery({
    queryKey: cacheKey,
    queryFn: async () => {
      // Build payload for RPC
      const payload: Record<string, any> = {};

      if (categoryId !== null && categoryId !== undefined) {
        payload.category_id = categoryId;
      }

      if (searchQuery && searchQuery.trim()) {
        payload.p_query = searchQuery.trim();
      }

      // Call the price range RPC
      const { data, error } = await supabase.rpc('get_price_range_for_filters', {
        payload,
      });

      if (error) {
        console.error('Price range fetch error:', error);
        throw new Error(`Failed to fetch price range: ${error.message}`);
      }

      // Parse response - RPC should return min_price and max_price
      if (data) {
        const minPrice = typeof data.min_price === 'number' ? data.min_price : (typeof data.data?.min_price === 'number' ? data.data.min_price : 0);
        const maxPrice = typeof data.max_price === 'number' ? data.max_price : (typeof data.data?.max_price === 'number' ? data.data.max_price : 1000);
        
        const result = {
          min: Math.max(minPrice, 0),
          max: Math.max(maxPrice, 0) || 1000, // Use actual max, or 1000 if max is 0/falsy
        };
        
        return result;
      }

      // Fallback defaults
      return { min: 0, max: 1000 };
    },
    enabled: enabled,
    staleTime: PRODUCT_CACHE_TIME.filters,
    gcTime: PRODUCT_CACHE_TIME.filters * 2,
    retry: 1,
  } as any);

  const result = {
    priceRange: (priceRangeQuery.data as { min: number; max: number } | undefined) ?? null,
    isLoading: priceRangeQuery.isLoading,
    isError: priceRangeQuery.isError,
    error: priceRangeQuery.error as Error | null,
    refetch: priceRangeQuery.refetch as () => void,
  };

  return result;
}
